<script>
    import dayjs from "dayjs";

    export let study = null;
    export let location = null;
    export let organization = null;
    export let product = null;
    /** e.g. "$199.00/month" — the price they can optionally continue at after the study */
    export let renewalPriceLabel = null;

    $: title = study?.title || 'Research study';
    $: site = location?.title || organization?.nme || '';
    $: orgName = organization?.nme || '';
    $: institution = site && orgName && site !== orgName ? `${site}` : (site || orgName);
    $: productName = product?.nme || 'OTO';

    $: accessLabel = study?.end_mode === 'duration' && study?.duration_months
        ? (study.duration_months === 1 ? '1 month from enrollment' : `${study.duration_months} months from enrollment`)
        : study?.end_date
            ? dayjs(study.end_date).format('D MMMM YYYY')
            : location?.research_end_date
                ? dayjs(location.research_end_date).format('D MMMM YYYY')
                : null;

    $: accessCaption = study?.end_mode === 'duration' ? 'Access for' : 'Access through';
</script>

<section class="study-banner">
    <div class="study-banner-top">
        <div class="study-intro">
            <div class="study-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M9 3h6" />
                    <path d="M10 3v5.2L5.4 17.2A3.2 3.2 0 0 0 8.2 22h7.6a3.2 3.2 0 0 0 2.8-4.8L14 8.2V3" />
                    <path d="M7.2 16h9.6" />
                </svg>
            </div>
            <div>
                <p class="study-kicker">Research study invitation</p>
                <h2>{title}</h2>
                {#if institution}
                    <p class="study-institution">{institution}</p>
                {/if}
            </div>
        </div>
        <div class="study-cost">
            <span class="cost-label">Cost to you</span>
            <strong>$0.00</strong>
            <span class="cost-sub">Fully sponsored</span>
        </div>
    </div>

    <dl class="study-meta">
        {#if site}
        <div>
            <dt>Study site</dt>
            <dd>{site}</dd>
        </div>
        {/if}
        <div>
            <dt>Kit &amp; subscription</dt>
            <dd>Provided by the study</dd>
        </div>
        {#if accessLabel}
        <div>
            <dt>{accessCaption}</dt>
            <dd>{accessLabel}</dd>
        </div>
        {/if}
    </dl>

    <p class="study-note">
        <i class="fa-solid fa-check" aria-hidden="true"></i>
        <span>
            You were sent this link because you are enrolled in <strong>{title}</strong>.
            We collect no payment method.
            {#if renewalPriceLabel}
                When the study ends you can optionally renew {productName} at <strong>{renewalPriceLabel}</strong> — nothing renews on its own.
            {:else}
                When the study ends you can optionally continue {productName} at the regular price — nothing renews on its own.
            {/if}
        </span>
    </p>
</section>

<style lang="scss">
    $teal: #1b8a6a;
    $teal-soft: #c7ddd4;
    $ink: #111827;
    $muted: #6b7280;

    .study-banner {
        --study-teal: #{$teal};
        font-family: Inter, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        background:
            linear-gradient(105deg, #d7efe6 0%, #eef7f3 38%, #ffffff 72%),
            #ffffff;
        border: 1px solid $teal-soft;
        border-radius: 16px;
        padding: 24px 26px 20px;
        text-align: left;
        color: #1f2937;
        margin-bottom: 8px;
    }

    .study-banner-top {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 28px;
    }

    .study-intro {
        display: flex;
        align-items: flex-start;
        gap: 14px;
        min-width: 0;
    }

    .study-icon {
        width: 44px;
        height: 44px;
        border-radius: 10px;
        background: $teal;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        svg {
            width: 22px;
            height: 22px;
        }
    }

    .study-kicker {
        margin: 0 0 6px;
        font-family: inherit;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: $teal;
    }

    h2 {
        margin: 0;
        font-family: inherit;
        font-size: 26px;
        font-weight: 700;
        line-height: 1.2;
        color: $ink;
        letter-spacing: -0.02em;
    }

    .study-institution {
        margin: 6px 0 0;
        font-size: 14px;
        color: $muted;
    }

    .study-cost {
        flex-shrink: 0;
        text-align: right;
        padding-left: 28px;
        border-left: 1px dashed #d1d5db;

        .cost-label {
            display: block;
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: #9ca3af;
        }

        strong {
            display: block;
            font-family: inherit;
            font-size: 32px;
            font-weight: 700;
            line-height: 1.1;
            margin: 2px 0;
            color: $ink;
        }

        .cost-sub {
            font-size: 13px;
            font-weight: 600;
            color: $teal;
        }
    }

    .study-meta {
        display: grid;
        grid-template-columns: 1fr;
        gap: 16px;
        margin: 20px 0 0;
        padding: 16px 0 0;
        border-top: 1px dotted #d1d5db;

        dt {
            font-size: 12px;
            color: $muted;
            margin-bottom: 2px;
        }

        dd {
            margin: 0;
            font-size: 14px;
            font-weight: 600;
            color: $ink;
        }
    }

    @media screen and (min-width: 720px) {
        .study-meta {
            grid-template-columns: repeat(3, minmax(0, 1fr));
        }
    }

    .study-note {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        margin: 16px 0 0;
        padding-top: 14px;
        border-top: 1px dotted #d1d5db;
        font-size: 13px;
        line-height: 1.45;
        color: #4b5563;

        i {
            color: $teal;
            margin-top: 2px;
        }

        strong {
            font-family: inherit;
            font-weight: 700;
            color: $ink;
        }
    }

    @media screen and (max-width: 640px) {
        .study-banner {
            padding: 18px 16px 16px;
            border-radius: 14px;
        }

        .study-banner-top {
            gap: 12px;
        }

        .study-intro {
            gap: 10px;
        }

        .study-icon {
            width: 36px;
            height: 36px;
            border-radius: 8px;

            svg {
                width: 18px;
                height: 18px;
            }
        }

        h2 {
            font-size: 20px;
        }

        .study-institution {
            font-size: 13px;
        }

        .study-cost {
            padding-left: 14px;

            strong {
                font-size: 24px;
            }
        }

        .study-meta {
            gap: 12px;
            margin-top: 16px;
            padding-top: 14px;
        }

        .study-note {
            font-size: 12px;
        }
    }
</style>
